/* ---------------------------------------------------------------------------
   The pieces every hand-drawn chart is made of.

   Construction Monitoring's site-record charts (src/screens/progressCharts.ts)
   and Bridge Rating's load-rating charts (src/rating/screens/ratingCharts.ts)
   are drawn the same way — plain SVG, coloured from the chart tokens in
   tokens.css, hover read off `data-tip` — so they share the one box, tooltip,
   SVG builder and geometry below rather than each keeping a copy. Styled by the
   `.pchart__*` rules in app.css.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';

/**
 * The box a chart and its tooltip sit in. Hover is read here, on an HTML
 * element whose handlers the reconciler patches, rather than on each mark:
 * any mark carrying `data-tip` is a target.
 */
export function chartBox(svg: SVGElement, onMove?: (e: PointerEvent, box: HTMLElement) => void): HTMLElement {
  const box = h(
    'div',
    {
      class: 'pchart__box',
      style: `--pchart-k: ${lastScale.get(svg.getAttribute('viewBox') ?? '') ?? 1}`,
      onpointermove: (e: Event) => {
        const box = e.currentTarget as HTMLElement;
        const pe = e as PointerEvent;
        if (onMove) return onMove(pe, box);
        const target = (pe.target as Element | null)?.closest('[data-tip]');
        if (!target) return hideTip(box);
        showTip(box, pe, (target.getAttribute('data-tip') ?? '').split('\n'));
      },
      onpointerleave: (e: Event) => hideTip(e.currentTarget as HTMLElement),
    },
    svg,
    h('div', { class: 'pchart__tip', role: 'tooltip', hidden: true }),
  );
  textScale?.observe(box);
  return box;
}

/**
 * SVG text scales with its box, so a chart drawn wider than its viewBox would
 * letter larger than the page around it. Where it is, the text is shrunk back
 * by the same factor (`--pchart-k`, read by the `.pchart__*` text rules);
 * where it is drawn narrower, the text shrinks with it, as it always has.
 *
 * Each repaint builds a fresh box and the reconciler copies its attributes
 * onto the one on screen, so a fresh box starts from the factor last measured
 * for its viewBox — otherwise every repaint would put the text back to full
 * scale until the next resize.
 */
const lastScale = new Map<string, number>();
const textScale = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver((entries) => {
  for (const e of entries) {
    const box = e.target as HTMLElement;
    const svg = box.querySelector('svg');
    const vb = svg?.viewBox.baseVal;
    const w = e.contentRect.width;
    if (!svg || !vb || vb.width <= 0 || w <= 0) continue;
    const k = Math.round(Math.min(1, vb.width / w) * 1000) / 1000;
    lastScale.set(svg.getAttribute('viewBox') ?? '', k);
    box.style.setProperty('--pchart-k', String(k));
  }
});

export function showTip(box: HTMLElement, e: PointerEvent, lines: string[]): void {
  const tip = box.querySelector<HTMLElement>('.pchart__tip');
  if (!tip) return;
  tip.replaceChildren(
    ...lines.map((l, i) => {
      const el = document.createElement('div');
      if (i === 0) el.className = 'pchart__tiphead';
      el.textContent = l;
      return el;
    }),
  );
  tip.hidden = false;
  const r = box.getBoundingClientRect();
  const px = e.clientX - r.left;
  const py = e.clientY - r.top;
  const flip = px > r.width - tip.offsetWidth - 24;
  tip.style.left = `${flip ? px - tip.offsetWidth - 14 : px + 14}px`;
  tip.style.top = `${Math.max(0, py - tip.offsetHeight - 10)}px`;
}

export function hideTip(box: HTMLElement): void {
  const tip = box.querySelector<HTMLElement>('.pchart__tip');
  if (tip) tip.hidden = true;
  box.querySelector('.pchart__cross')?.setAttribute('visibility', 'hidden');
}

/* --------------------------- geometry --------------------------- */

export function r1(v: number): number {
  return Math.round(v * 10) / 10;
}

export function scale(d0: number, d1: number, r0: number, r1v: number): (v: number) => number {
  const span = d1 - d0 || 1;
  return (v) => r0 + ((v - d0) / span) * (r1v - r0);
}

/** Round, clean ticks from zero to at least `max`, about four of them — whole steps of 1, 2 or 5. */
export function niceTicks(max: number): number[] {
  if (max <= 0) return [0, 1];
  const raw = max / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((f) => f * mag).find((s) => s >= raw) ?? 10 * mag;
  const out: number[] = [];
  for (let v = 0; v < max + step * 0.001; v += step) out.push(Math.round(v * 1000) / 1000);
  if (out[out.length - 1]! < max) out.push(Math.round((out[out.length - 1]! + step) * 1000) / 1000);
  return out;
}

/** A column from the baseline `y0`, `h` tall (negative hangs down), rounded at its data end. */
export function vbar(x: number, y0: number, w: number, hgt: number, rad = 4): string {
  const r = Math.min(rad, w / 2, Math.abs(hgt));
  if (hgt >= 0) {
    const t = y0 - hgt;
    return `M${r1(x)},${r1(y0)}V${r1(t + r)}Q${r1(x)},${r1(t)} ${r1(x + r)},${r1(t)}H${r1(x + w - r)}Q${r1(x + w)},${r1(t)} ${r1(x + w)},${r1(t + r)}V${r1(y0)}Z`;
  }
  const b = y0 - hgt;
  return `M${r1(x)},${r1(y0)}V${r1(b - r)}Q${r1(x)},${r1(b)} ${r1(x + r)},${r1(b)}H${r1(x + w - r)}Q${r1(x + w)},${r1(b)} ${r1(x + w)},${r1(b - r)}V${r1(y0)}Z`;
}

/** A horizontal bar from `x`, rounded at its data end. */
export function hbar(x: number, y: number, w: number, hgt: number): string {
  return segment(x, y, w, hgt, 0, 4);
}

/** A horizontal segment with its own left and right corner radii. */
export function segment(x: number, y: number, w: number, hgt: number, rl: number, rr: number): string {
  const a = Math.min(rl, w / 2, hgt / 2);
  const b = Math.min(rr, w / 2, hgt / 2);
  return (
    `M${r1(x + a)},${r1(y)}H${r1(x + w - b)}Q${r1(x + w)},${r1(y)} ${r1(x + w)},${r1(y + b)}` +
    `V${r1(y + hgt - b)}Q${r1(x + w)},${r1(y + hgt)} ${r1(x + w - b)},${r1(y + hgt)}` +
    `H${r1(x + a)}Q${r1(x)},${r1(y + hgt)} ${r1(x)},${r1(y + hgt - a)}V${r1(y + a)}Q${r1(x)},${r1(y)} ${r1(x + a)},${r1(y)}Z`
  );
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** `h` for SVG, which has to be created in its own namespace. */
export function sv(
  tag: string,
  attrs: Record<string, string | number | null | undefined>,
  ...kids: (SVGElement | string | null | false | undefined)[]
): SVGElement {
  const el = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) if (v !== null && v !== undefined) el.setAttribute(k, String(v));
  for (const k of kids) {
    if (k === null || k === false || k === undefined) continue;
    el.appendChild(typeof k === 'string' ? document.createTextNode(k) : k);
  }
  return el;
}
